import { useState, useRef, useEffect } from "react";
import { EditorToolbar } from "./EditorToolbar";
import { EditorActions } from "./EditorActions";
import { ReminderDialog } from "./ReminderDialog";
import { useDraftOperations } from "@/hooks/useDraftOperations";
import { PreviewPanel } from "./PreviewPanel";
import { EditorImageSection } from "./editor/EditorImageSection";
import { SchedulingBar } from "./editor/SchedulingBar";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { useCompanySuggestions } from "@/hooks/useCompanySuggestions";
import { usePostHog } from 'posthog-js/react'
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { FeedbackDialog } from "@/components/feedback/FeedbackDialog";
import { QuickActionBar } from "./editor/QuickActionBar";

interface DraftEditorProps {
  initialContent?: string;
  draftId?: string;
  onSaveDraft?: () => void;
  onSchedule?: () => void;
  isDialog?: boolean;
  contentType?: "draft" | "scheduled";
  scheduledFor?: string;
  initialImages?: string[];
  initialVideos?: string[];
}

interface CompanySuggestion {
  linkedin_internal_id: string;
  name: string;
  tagline: string;
  profile_pic_url: string;
  urn: string;
  universal_company_link: string;
}

export const DraftEditor = ({
  initialContent = "",
  draftId,
  onSaveDraft,
  onSchedule,
  isDialog,
  contentType = "draft",
  scheduledFor,
  initialImages = [],
  initialVideos = [],
}: DraftEditorProps) => {
  const [isReminderDialogOpen, setIsReminderDialogOpen] = useState(false);
  const [content, setContent] = useState(initialContent);
  const [selectedImages, setSelectedImages] = useState<string[]>(initialImages);
  const [selectedVideo, setSelectedVideo] = useState<string | null>(initialVideos[0] || null);
  const [currentScheduledFor, setCurrentScheduledFor] = useState(scheduledFor);
  const editorRef = useRef<HTMLDivElement>(null);
  const [mentionSearch, setMentionSearch] = useState("");
  const [showMentions, setShowMentions] = useState(false);
  const [mentionPosition, setMentionPosition] = useState({ x: 0, y: 0 });
  const { suggestions, loading } = useCompanySuggestions(mentionSearch);
  const posthog = usePostHog()
  const showCompanyMentions = posthog?.isFeatureEnabled('show-linkedin-company-mentions')
  const [isFeedbackOpen, setIsFeedbackOpen] = useState(false);

  const { handleSaveDraft, handlePublish: _handlePublish } = useDraftOperations(
    editorRef.current?.textContent || "",
    draftId,
    onSaveDraft,
    () => setContent(""),
    contentType
  );

  useEffect(() => {
    if (editorRef.current) {
      editorRef.current.innerHTML = content;
    }
  }, []);

  const handleScheduleUpdate = async (newDate: string) => {
    if (!draftId) return;

    const { error } = await supabase
      .from('scheduled_posts')
      .update({ 
        scheduled_for: newDate,
        updated_at: new Date().toISOString(),
        images: selectedImages,
        video: selectedVideo
      })
      .eq('id', draftId);

    if (error) {
      toast.error("Failed to update schedule time");
      return;
    }

    setCurrentScheduledFor(newDate);
    toast.success("Schedule updated successfully!");
    onSaveDraft?.();
  };

  const handleScheduleReminder = () => {
    if (editorRef.current) {
      const formattedContent = editorRef.current.innerHTML;
      console.log('Formatted content for schedule:', formattedContent);
      setContent(formattedContent);
      setIsReminderDialogOpen(true);
    }
  };

  const handleReminderScheduled = () => {
    setIsReminderDialogOpen(false);
    onSchedule?.();
    setContent("");
  };

  const handleContentUpdate = (newContent: string) => {
    setContent(newContent);
    // Only update innerHTML if the content is coming from an external source (like AI)
    if (editorRef.current && !editorRef.current.contains(document.activeElement)) {
      editorRef.current.innerHTML = newContent;
    }
  };

  const handleImagesGenerated = (images: string[]) => {
    setSelectedImages(images);
  };

  const handleImageSelect = (images: string[]) => {
    setSelectedImages(images);
  };

  const handleImageUpload = (imageUrl: string) => {
    setSelectedImages([...selectedImages, imageUrl]);
  };

  const handleImageRemove = (imageUrl: string) => {
    setSelectedImages(selectedImages.filter(img => img !== imageUrl));
  };

  const handleVideoUpload = (videoUrl: string) => {
    setSelectedVideo(videoUrl);
    setSelectedImages([]);
  };

  const handleKeyUp = (e: React.KeyboardEvent) => {
    if (!showCompanyMentions) return;
    
    const selection = window.getSelection();
    if (!selection?.anchorNode || !editorRef.current) return;

    const text = selection.anchorNode.textContent || "";
    const position = selection.anchorOffset;
    
    // Find the last @ symbol before the cursor
    const lastAtSymbol = text.lastIndexOf('@', position);
    if (lastAtSymbol >= 0) {
      const searchText = text.slice(lastAtSymbol + 1, position);
      if (searchText.length >= 1) {
        setMentionSearch(searchText);
        setShowMentions(true);
        
        // Calculate position for mention popup
        const range = selection.getRangeAt(0);
        const rect = range.getBoundingClientRect();
        const editorRect = editorRef.current.getBoundingClientRect();
        
        // Position relative to the editor
        const x = Math.min(
          rect.left - editorRect.left,
          editorRect.width - 200 // ensure popup doesn't overflow horizontally
        );
        
        const y = Math.min(
          rect.bottom - editorRect.top + 8, // Add 8px spacing below the text
          editorRect.height - 200 // ensure popup doesn't overflow vertically
        );
        
        setMentionPosition({ x, y });
      } else {
        // Clear suggestions if search text is less than 2 characters
        setShowMentions(false);
        setMentionSearch("");
      }
    } else {
      // No @ symbol found, clear suggestions
      setShowMentions(false);
      setMentionSearch("");
    }
  };

  const insertMention = (company: CompanySuggestion) => {
    const selection = window.getSelection();
    if (!selection?.anchorNode || !editorRef.current) return;

    const range = selection.getRangeAt(0);
    const text = selection.anchorNode.textContent || "";
    const position = selection.anchorOffset;
    
    // Find the starting position of the @mention
    const lastAtSymbol = text.lastIndexOf('@', position);
    if (lastAtSymbol >= 0) {
      // Create the mention span
      const mentionSpan = document.createElement('span');
      mentionSpan.className = 'text-blue-600 font-bold';
      mentionSpan.contentEditable = 'false';
      mentionSpan.dataset.urn = company.urn;
      mentionSpan.textContent = company.name;

      // Replace the @mention text with the span
      range.setStart(selection.anchorNode, lastAtSymbol);
      range.setEnd(selection.anchorNode, position);
      range.deleteContents();
      range.insertNode(mentionSpan);
      
      // Add a space after the mention
      const space = document.createTextNode(' ');
      range.setStartAfter(mentionSpan);
      range.insertNode(space);
      
      // Move cursor after the space
      range.setStartAfter(space);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);

      // Update the content state with the new HTML
      if (editorRef.current) {
        handleContentUpdate(editorRef.current.innerHTML);
      }
    }

    setShowMentions(false);
    setMentionSearch("");
  };

  const formatContentForLinkedIn = (content: string): string => {
    if (!showCompanyMentions) return content;
    
    //console.log('formatContentForLinkedIn called with:', content);
    const container = document.createElement('div');
    container.innerHTML = content;
    
    // Convert span mentions to LinkedIn format
    const mentions = container.querySelectorAll('span[data-urn]');
    //console.log('Found mentions:', mentions.length);
    mentions.forEach(mention => {
      const name = mention.textContent;
      const urn = mention.getAttribute('data-urn');
      mention.replaceWith(`@[${name}](${urn})`);
    });
    
    const result = container.textContent || '';
    //console.log('Formatted content:', result);
    return result;
  };

  const handleSaveDraftWithMentions = () => {
    //console.log('handleSaveDraftWithMentions called');
    if (editorRef.current) {
      //console.log('Editor content:', editorRef.current.innerHTML);
      const formattedContent = editorRef.current.innerHTML //formatContentForLinkedIn(editorRef.current.innerHTML);
      //console.log('Saving formatted content:', formattedContent);
      handleSaveDraft(formattedContent, selectedImages, selectedVideo);
    }
  };

  const handlePublishWithMentions = () => {
    //console.log('handlePublishWithMentions called');
    if (editorRef.current) {
      console.log('Editor content:', editorRef.current.innerHTML);
      const formattedContent = editorRef.current.innerHTML //formatContentForLinkedIn(editorRef.current.innerHTML);
      console.log('Publishing formatted content:', formattedContent);
      return formattedContent;
    }
    return content;
  };

  // Add keydown handler for backspace/delete
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Backspace' || e.key === 'Delete') {
      const selection = window.getSelection();
      if (!selection?.anchorNode) return;

      // Check if we're at the edge of or inside a mention span
      let currentNode = selection.anchorNode;
      let mentionSpan = null;

      // If we're in a text node, check if parent is a mention span
      if (currentNode.nodeType === Node.TEXT_NODE) {
        if (currentNode.parentElement?.matches('span[data-urn]')) {
          mentionSpan = currentNode.parentElement;
        }
      } 
      // If we're directly in the mention span
      else if (currentNode.nodeType === Node.ELEMENT_NODE && (currentNode as Element).matches('span[data-urn]')) {
        mentionSpan = currentNode as HTMLElement;
      }

      // If we found a mention span, remove it entirely
      if (mentionSpan) {
        e.preventDefault();
        mentionSpan.remove();
        // Add a space to replace the mention
        const space = document.createTextNode(' ');
        selection.getRangeAt(0).insertNode(space);
        // Move cursor after the space
        const range = document.createRange();
        range.setStartAfter(space);
        range.collapse(true);
        selection.removeAllRanges();
        selection.addRange(range);
        
        // Update content
        if (editorRef.current) {
          handleContentUpdate(editorRef.current.innerHTML);
        }
      }
    }
  };

  // Add paste handler to strip formatting
  const handlePaste = (e: React.ClipboardEvent) => {
    e.preventDefault();
    const text = e.clipboardData.getData('text/plain');
    document.execCommand('insertText', false, text);
  };

  return (
    <div className="space-y-4">
      <EditorActions
        onSaveDraft={handleSaveDraftWithMentions}
        onSchedule={handleScheduleReminder}
        onPublish={handlePublishWithMentions}
        contentType={contentType}
        content={content}
        images={selectedImages}
        video={selectedVideo}
      />

      <div className="grid grid-cols-1 gap-4">
        <div className="space-y-4">
          {contentType === "scheduled" && (
            <SchedulingBar
              scheduledFor={currentScheduledFor}
              onScheduleUpdate={handleScheduleUpdate}
            />
          )}
          <EditorToolbar 
            onContentUpdate={handleContentUpdate} 
            onImageUpload={handleImageUpload}
            onVideoUpload={handleVideoUpload}
          />
          <div className="relative rounded-lg focus-within:ring-[1.5px] focus-within:ring-ring focus-within:border-ring border">
            <div
              ref={editorRef}
              className="min-h-[200px] max-h-[600px] overflow-y-auto p-4 pb-12 whitespace-pre-wrap focus:outline-none"
              contentEditable
              onInput={(e) => handleContentUpdate(e.currentTarget.innerHTML)}
              onKeyUp={handleKeyUp}
              onKeyDown={showCompanyMentions ? handleKeyDown : undefined}
              onPaste={handlePaste}
            />
            <div className="absolute bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent pointer-events-none rounded-b-lg" />
            <div className="absolute bottom-2 left-1/2 transform -translate-x-1/2">
              <QuickActionBar 
                content={content}
                onContentUpdate={handleContentUpdate}
              />
            </div>

            {/* Company mention suggestions popup */}
            {showCompanyMentions && showMentions && (
              <div
                className="absolute bg-white shadow-lg rounded-lg border p-2 z-50 max-h-[200px] w-[400px] overflow-y-auto"
                style={{ 
                  left: `${mentionPosition.x}px`, 
                  top: `${mentionPosition.y}px`,
                  transform: 'translateY(2.5em)'
                }}
              >
                {loading ? (
                  <div className="p-2">Loading...</div>
                ) : suggestions.length > 0 ? (
                  <ul className="space-y-1">
                    {suggestions.map((company) => (
                      <button
                        key={company.linkedin_internal_id}
                        className="flex items-center gap-3 px-4 py-2 rounded-lg w-full text-left transition-all duration-200 font-normal hover:bg-gray-50"
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          insertMention(company);
                        }}
                      >
                        <div className="flex items-center gap-2 w-full">
                          {company.profile_pic_url && (
                            <img
                              src={company.profile_pic_url}
                              alt={company.name}
                              className="w-6 h-6 rounded flex-shrink-0"
                            />
                          )}
                          <div className="flex flex-col">
                            <span className="truncate text-gray-600">{company.name}</span>
                            <span className="text-xs text-gray-400">Company</span>
                          </div>
                        </div>
                      </button>
                    ))}
                  </ul>
                ) : (
                  <div className="p-2 text-sm text-muted-foreground">
                    Can't find the company you're looking for?{" "}
                    <span
                      onClick={() => setIsFeedbackOpen(true)}
                      className="text-primary hover:underline cursor-pointer"
                    >
                      Let us know
                    </span>
                    .
                  </div>
                )}
              </div>
            )}
          </div>
          <EditorImageSection
            content={content}
            onImagesGenerated={handleImagesGenerated}
            onImageSelect={handleImageSelect}
            onContentUpdate={handleContentUpdate}
          />
          <PreviewPanel 
            content={content} 
            images={selectedImages}
            video={selectedVideo}
            onImageRemove={handleImageRemove}
            onVideoRemove={() => setSelectedVideo(null)}
          />
        </div>
      </div>

      <ReminderDialog
        open={isReminderDialogOpen}
        onOpenChange={setIsReminderDialogOpen}
        content={editorRef.current?.innerHTML || ""}
        onScheduled={handleReminderScheduled}
        images={selectedImages}
        video={selectedVideo}
      />

      <Dialog open={isFeedbackOpen} onOpenChange={setIsFeedbackOpen}>
        <DialogContent className="sm:max-w-[425px] pt-10">
          <FeedbackDialog 
            onClose={() => setIsFeedbackOpen(false)}
            initialFeedback={`I can't find the following company: ${mentionSearch}`}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};